import { type ChangeEvent, useRef } from "react";
import { Camera, Trash2 } from "lucide-react";
import { PHOTO_UPLOADS_ENABLED } from "@shared/constants/features";
import { ChildAvatar } from "../ui/ChildAvatar";
import { Spinner } from "../ui/Spinner";
import { fileToAvatarDataUri } from "../../lib/img";
import { useSetChildPhoto, useClearChildPhoto } from "../../lib/api/useChildren";
import { useToast } from "../../lib/toast/ToastProvider";
import { ACCENTS } from "../../lib/accents";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";

interface ChildPhotoUploaderProps {
  childId: string;
  name: string;
  photoUrl: string | null;
}

/**
 * The avatar at the centre of the profile hero.
 *
 * While PHOTO_UPLOADS_ENABLED is false (BRIEF §5.3) there is NO upload UI: it is
 * the plain nickname-initial chip in the same gradient ring, with no camera,
 * no file input and no click target. Flip the switch and the uploader below
 * comes back unchanged. (A photo stored before the switch can still be removed
 * from the profile: see StoredPhotoNotice.)
 */
export function ChildPhotoUploader(props: ChildPhotoUploaderProps) {
  if (!PHOTO_UPLOADS_ENABLED) return <ChildProfileAvatar name={props.name} />;
  return <PhotoUploader {...props} />;
}

function ChildProfileAvatar({ name }: { name: string }) {
  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 rounded-full bg-gradient-to-br p-1 shadow-soft",
        ACCENTS.sky.gradient,
      )}
    >
      <ChildAvatar name={name} className="h-16 w-16 ring-2 ring-white" textClassName="text-2xl" />
    </span>
  );
}

/**
 * The child's profile photo, presented as the interactive centerpiece of the
 * profile hero: the avatar itself IS the upload target. A camera badge invites a
 * first photo (or a swap), a hover/focus scrim reinforces that it's clickable,
 * and a trash badge appears only once a photo is set. The file is resized
 * in-browser (fileToAvatarDataUri) before it ever leaves the device, then saved
 * via PUT /api/children/:id/photo. Rendered only while PHOTO_UPLOADS_ENABLED.
 */
function PhotoUploader({ childId, name, photoUrl }: ChildPhotoUploaderProps) {
  const setPhoto = useSetChildPhoto(childId);
  const clearPhoto = useClearChildPhoto(childId);
  const { publish } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const hasPhoto = Boolean(photoUrl);
  const busy = setPhoto.isPending || clearPhoto.isPending;

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking the same file still fires change
    if (!file) return;
    try {
      const dataUri = await fileToAvatarDataUri(file);
      await setPhoto.mutateAsync(dataUri);
      publish({ variant: "success", title: "Photo updated" });
    } catch (err) {
      publish({
        variant: "error",
        title: "Couldn't update photo",
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const onRemove = async () => {
    try {
      await clearPhoto.mutateAsync();
      publish({ variant: "success", title: "Photo removed" });
    } catch (err) {
      publish({
        variant: "error",
        title: "Couldn't remove photo",
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <span className="relative inline-flex shrink-0">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onFile}
      />

      {/* The avatar itself is the upload button. */}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        aria-label={hasPhoto ? "Change photo" : "Add photo"}
        aria-busy={busy}
        className={cn(
          "group relative inline-flex rounded-full bg-gradient-to-br p-1 shadow-soft transition active:scale-95 disabled:cursor-not-allowed",
          ACCENTS.sky.gradient,
          FOCUS_RING,
        )}
      >
        <ChildAvatar
          name={name}
          photoUrl={photoUrl}
          className="h-16 w-16 ring-2 ring-white"
          textClassName="text-2xl"
        />

        {/* Hover / focus / busy scrim over the image. */}
        <span
          aria-hidden
          className={cn(
            "absolute inset-1 flex items-center justify-center rounded-full bg-neutral-900/40 text-white transition",
            busy
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
          )}
        >
          {busy ? (
            <Spinner size="sm" className="text-white" />
          ) : (
            <Camera className="h-5 w-5" aria-hidden />
          )}
        </span>

        {/* Persistent affordance: "this photo is editable". */}
        <span className="absolute -bottom-0.5 -right-0.5 grid h-6 w-6 place-items-center rounded-full bg-white text-sky-600 shadow-soft ring-2 ring-white">
          <Camera className="h-3.5 w-3.5" aria-hidden />
        </span>
      </button>

      {hasPhoto && (
        <button
          type="button"
          onClick={() => void onRemove()}
          disabled={busy}
          aria-label="Remove photo"
          className={cn(
            "absolute -right-0.5 -top-0.5 z-10 grid h-6 w-6 place-items-center rounded-full bg-white text-neutral-500 shadow-soft ring-2 ring-white transition hover:text-red-600 disabled:opacity-50",
            FOCUS_RING,
          )}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </span>
  );
}
